import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, Paperclip, Award, Pencil, BellRing, PlayCircle } from "lucide-react";
import { toast } from "sonner";

function embedUrl(url) {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

async function openFile(path, download) {
  const win = download ? null : window.open("", "_blank");
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    if (win) { win.location.href = url; }
    else { const a = document.createElement("a"); a.href = url; a.download = download; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { win?.close(); toast.error(formatApiError(e)); }
}

function VideoAttachment({ path, a }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const play = async () => {
    setLoading(true);
    try { const r = await api.get(path, { responseType: "blob" }); setSrc(URL.createObjectURL(r.data)); }
    catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => () => { if (src) { URL.revokeObjectURL(src); } }, [src]);
  return src ? (
    <video src={src} controls playsInline className="w-full rounded-xl bg-black" data-testid={`video-player-${a.id}`} />
  ) : (
    <button onClick={play} disabled={loading} className="w-full aspect-video rounded-xl bg-slate-900 text-white flex flex-col items-center justify-center gap-2 hover:bg-slate-800 transition-colors" data-testid={`video-play-${a.id}`}>
      <PlayCircle className="w-12 h-12" />
      <span className="text-sm">{loading ? "Caricamento video…" : a.filename}</span>
    </button>
  );
}

function Lesson({ c, l, done, onDone, isAdmin }) {
  const emb = l.video_url && embedUrl(l.video_url);
  return (
    <article className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid={`lesson-${l.id}`}>
      <h3 className="font-display text-xl font-bold text-slate-900">{l.title}</h3>
      {l.content && <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{l.content}</div>}
      {emb && <div className="aspect-video"><iframe src={emb} title={l.title} className="w-full h-full rounded-xl" allowFullScreen /></div>}
      {l.video_url?.startsWith("https://") && !emb && <a href={l.video_url} target="_blank" rel="noreferrer" className="text-sm text-sky-700 font-semibold">Guarda il video</a>}
      {(l.attachments || []).filter((a) => a.content_type?.startsWith("video/")).map((a) => (
        <VideoAttachment key={a.id} a={a} path={`/courses/${c.id}/lessons/${l.id}/attachments/${a.id}`} />
      ))}
      {(l.attachments || []).some((a) => !a.content_type?.startsWith("video/")) && (
        <div className="flex flex-wrap gap-2">
          {l.attachments.filter((a) => !a.content_type?.startsWith("video/")).map((a) => (
            <button key={a.id} onClick={() => openFile(`/courses/${c.id}/lessons/${l.id}/attachments/${a.id}`)}
              className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1.5 transition-colors" data-testid={`attachment-${a.id}`}>
              <Paperclip className="w-3 h-3" /> {a.filename}
            </button>
          ))}
        </div>
      )}
      {!isAdmin && (done ? (
        <div className="text-sm font-semibold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Lezione completata</div>
      ) : (
        <Button onClick={onDone} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`complete-lesson-${l.id}`}>Ho letto e capito</Button>
      ))}
    </article>
  );
}

function Quiz({ c, onResult }) {
  const [answers, setAnswers] = useState({});
  const [res, setRes] = useState(null);
  const submit = async () => {
    if (Object.keys(answers).length < c.quiz.length) return toast.error("Rispondi a tutte le domande");
    try { const { data } = await api.post(`/courses/${c.id}/quiz`, { answers }); setRes(data); onResult(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5" data-testid="quiz-section">
      <h3 className="font-display text-xl font-bold text-slate-900">Quiz finale <span className="text-sm font-normal text-slate-500">(minimo {c.pass_score}%)</span></h3>
      {c.quiz.map((q, i) => (
        <fieldset key={q.id} className={`space-y-2 rounded-xl p-3 ${res?.wrong.includes(q.id) ? "bg-rose-50 border border-rose-200" : ""}`}>
          <legend className="font-semibold text-sm text-slate-900">{i + 1}. {q.question}</legend>
          {q.options.map((o, k) => (
            <label key={k} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="radio" name={q.id} checked={answers[q.id] === k} onChange={() => { setAnswers({ ...answers, [q.id]: k }); setRes(null); }} data-testid={`quiz-${i}-option-${k}`} /> {o}
            </label>
          ))}
        </fieldset>
      ))}
      {res && (
        <div className={`rounded-xl p-4 text-sm font-semibold ${res.passed ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="quiz-result">
          {res.passed ? `Superato con ${res.score}%!` : `Punteggio ${res.score}%: rileggi le lezioni e riprova. Le domande sbagliate sono evidenziate.`}
        </div>
      )}
      <Button onClick={submit} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="quiz-submit-button">Invia risposte</Button>
    </section>
  );
}

function ProgressTable({ courseId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/courses/${courseId}/progress`).then((r) => setRows(r.data)); }, [courseId]);
  const remind = async () => { const { data } = await api.post(`/courses/${courseId}/remind`); toast.success(`Promemoria inviato a ${data.sent} dipendenti`); };
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="course-progress-table">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold text-slate-900">Progressi dei dipendenti</h3>
        <Button variant="outline" size="sm" onClick={remind} data-testid="course-remind-button"><BellRing className="w-4 h-4 mr-1" /> Sollecita chi non ha finito</Button>
      </div>
      <ul className="divide-y divide-slate-100 text-sm">
        {rows.map((r) => (
          <li key={r.user_id} className="py-2 flex flex-wrap items-center gap-3">
            <span className="font-semibold text-slate-900 flex-1">{r.name}</span>
            <span className="text-slate-500">{r.lessons_done}/{r.lessons_total} lezioni</span>
            <span className="text-slate-500">miglior quiz {r.best_score}% ({r.attempts} tentativi)</span>
            {r.passed ? <span className="text-emerald-700 font-bold">SUPERATO</span> : <span className="text-amber-700 font-bold">IN CORSO</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function CourseView() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [c, setC] = useState(null);
  const load = () => api.get(`/courses/${id}`).then((r) => setC(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!c) return <div className="text-slate-400 text-sm">Caricamento…</div>;
  const done = new Set(c.progress?.completed_lessons || []);
  const allDone = c.lessons.every((l) => done.has(l.id));
  const complete = async (lid) => {
    try { await api.post(`/courses/${c.id}/lessons/${lid}/complete`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 max-w-3xl" data-testid="course-view">
      <header className="space-y-2">
        <Link to="/formazione" className="text-xs text-sky-700 font-semibold">← Tutti i corsi</Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">{c.title}</h1>
          {isAdmin && <Link to={`/formazione/${c.id}/modifica`}><Button variant="outline" data-testid="course-edit-button"><Pencil className="w-4 h-4 mr-2" /> Modifica</Button></Link>}
        </div>
        {c.description && <p className="text-slate-600 text-sm whitespace-pre-wrap">{c.description}</p>}
        {!isAdmin && (
          <div className="flex flex-wrap gap-2 pt-2">
            {c.lessons.map((l, i) => (
              <span key={l.id} className="inline-flex items-center gap-1 text-xs text-slate-600">
                {done.has(l.id) ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-slate-300" />} {i + 1}
              </span>
            ))}
          </div>
        )}
      </header>
      {c.progress?.passed && (
        <div className="flex flex-wrap items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="course-passed-banner">
          <Award className="w-6 h-6 text-emerald-600" />
          <span className="flex-1 text-sm font-semibold text-emerald-800">Corso superato con {c.progress.best_score}%</span>
          <Button onClick={() => openFile(`/courses/${c.id}/certificate`, "attestato.pdf")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="certificate-download-button">Scarica attestato</Button>
        </div>
      )}
      {c.lessons.map((l) => <Lesson key={l.id} c={c} l={l} done={done.has(l.id)} onDone={() => complete(l.id)} isAdmin={isAdmin} />)}
      {!isAdmin && c.quiz.length > 0 && !c.progress?.passed && (allDone ? <Quiz c={c} onResult={load} /> : (
        <p className="text-sm text-slate-500 bg-slate-100 rounded-xl p-4">Completa tutte le lezioni per sbloccare il quiz finale.</p>
      ))}
      {isAdmin && <ProgressTable courseId={c.id} />}
    </div>
  );
}
